/**
 * 결재 서식의 **계산식** — 서버와 화면이 **같은 규칙으로** 센다 (2026-09-01 · 판 13).
 *
 * ★★★ **왜 공유 패키지에 두나.** 화면은 사람이 타이핑하는 동안 값을 보여 줘야 하고, 서버는 올릴 때
 *    그 값을 **다시 세서** 저장해야 한다(전결을 가르는 숫자를 브라우저가 정하게 두면 결재선을
 *    고쳐 보낼 수 있다). 규칙이 두 벌이면 «화면에 보인 값»과 «저장된 값»이 다른 날이 온다.
 *
 * ★★ **작게 닫는다.** 넷연산·괄호·함수 다섯이 전부다. 조건식도, 반복도, 다른 문서 참조도 없다 —
 *    조건이 들어오는 순간 「이 문서는 왜 이 결재선인가」를 사람이 읽어 낼 수 없게 되고, 그때부터
 *    서식은 프로그램이 된다. 회사 관리자가 감당할 물건이 아니다.
 *
 * ★★ **모르면 «빈 값»이지 0 이 아니다.** 표가 비었거나 시각 한쪽이 안 적혔으면 `null` 을 준다.
 *    0 으로 보면 **전부 전결**이 된다(휴가 판에서 이미 얻은 규칙).
 *
 * ★ **못 읽은 칸을 숨기지 않는다.** 「금액」 열에 `3만원` 처럼 적히면 그 줄은 못 센다 —
 *   합계와 함께 `unread` 를 돌려줘 화면이 「3줄 중 1줄을 못 읽었습니다」를 말할 수 있게 한다.
 *   조용히 빼면 합계가 조용히 틀린다.
 *
 * 어휘
 *   [칸]            칸 참조 — **칸의 열쇠**로 적는다(이름을 바꿔도 식이 안 깨진다)
 *   [칸.열]         표의 한 열
 *   합계(...)       SUM    · 표 한 열을 다 더한다
 *   개수(...)       COUNT  · 표의 줄 수(빈 줄은 안 센다)
 *   시간차(a, b)    두 시각의 차 — **시간 단위**(18:30~21:00 → 2.5)
 *   일수(기간)      기간의 날 수 — 시작과 끝을 **양쪽 다** 센다(하루면 1)
 *   반올림(x, n)    ROUND
 *   + - * / ( )     넷연산과 괄호 (− × ÷ 도 받는다)
 */

export const FORMULA_MAX_LEN = 500;

/** 표 열의 종류 — 합계를 세려면 그 열이 숫자여야 한다. */
/**
 * 표의 **열 종류**.
 *
 * ★★ **끝에만 보탠다** — 화면 사본과 옛 서식이 자리로 읽는 자리가 있다.
 * ★ `select` 는 2026-09-04(예산 판 B0)에 보탰다. 그 열의 칸은 **코드와 이름을 함께**
 *   담는다(`{code,label}`) — 계정과목처럼 **코드로 갈리는 것**을 표 안에 두기 위해서다.
 *   셈에는 안 쓴다(합계는 `number`·`money` 열만 센다).
 */
export const COLUMN_KINDS = ['text', 'number', 'date', 'money', 'select'] as const;
export type ColumnKind = (typeof COLUMN_KINDS)[number];

export interface FormulaError { code: string; message: string }

/** 계산식이 무엇을 가리키는가 — 저장 전에 «그 칸이 있나» 를 보는 데 쓴다. */
export interface FormulaRefs {
  /** `[칸]` 으로 참조된 칸의 열쇠들. */
  fields: string[];
  /** `[칸.열]` 로 참조된 것들. */
  columns: Array<{ field: string; column: string }>;
}

/** 값을 어디서 읽어 오나 — 서버는 저장된 값에서, 화면은 지금 입력에서 준다. */
export interface FormulaContext {
  /** 칸 하나의 값 (숫자 칸 · 시각 칸 · 기간 칸 · 다른 계산식의 결과). */
  field: (key: string) => unknown;
  /** 표 한 열의 값들 — 줄마다 문자열 하나. */
  column: (field: string, column: string) => string[];
}

export interface FormulaResult {
  /** 셈한 값. **모르면 `null`** — 0 이 아니다. */
  value: number | null;
  /** 숫자로 못 읽은 칸 수 — 화면이 그 사실을 말한다. */
  unread: number;
  /** 셈에 쓴 칸 수(표 줄 포함) — 「3줄 중 1줄」의 분모. */
  read: number;
}

// ── 토큰 ────────────────────────────────────────────────────────────
type Tok =
  | { t: 'num'; v: number }
  | { t: 'ref'; field: string; column: string | null }
  | { t: 'fn'; name: string }
  | { t: 'op'; v: '+' | '-' | '*' | '/' }
  | { t: '(' } | { t: ')' } | { t: ',' };

/** 한국어 이름을 정본으로 두되, 엑셀을 쓰던 손이 그대로 얹히도록 영어 이름도 받는다. */
const FN_ALIAS: Record<string, string> = {
  SUM: '합계', COUNT: '개수', ROUND: '반올림',
  합계: '합계', 개수: '개수', 시간차: '시간차', 일수: '일수', 반올림: '반올림',
};
const FN_ARITY: Record<string, [number, number]> = {
  합계: [1, 1], 개수: [1, 1], 시간차: [2, 2], 일수: [1, 1], 반올림: [1, 2],
};

const OP_ALIAS: Record<string, '+' | '-' | '*' | '/'> = {
  '+': '+', '-': '-', '*': '*', '/': '/', '−': '-', '×': '*', '÷': '/',
};

function tokenize(src: string): Tok[] | FormulaError {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (/\s/.test(c)) { i += 1; continue; }
    if (c === '(') { out.push({ t: '(' }); i += 1; continue; }
    if (c === ')') { out.push({ t: ')' }); i += 1; continue; }
    if (c === ',') { out.push({ t: ',' }); i += 1; continue; }
    if (OP_ALIAS[c]) { out.push({ t: 'op', v: OP_ALIAS[c]! }); i += 1; continue; }
    if (c === '[') {
      const end = src.indexOf(']', i + 1);
      if (end < 0) return { code: 'FORMULA_UNCLOSED_REF', message: '칸 참조의 «]» 가 없습니다' };
      const raw = src.slice(i + 1, end).trim();
      if (raw === '') return { code: 'FORMULA_EMPTY_REF', message: '빈 칸 참조가 있습니다' };
      const dot = raw.indexOf('.');
      out.push(dot < 0
        ? { t: 'ref', field: raw, column: null }
        : { t: 'ref', field: raw.slice(0, dot).trim(), column: raw.slice(dot + 1).trim() });
      i = end + 1;
      continue;
    }
    if (/[0-9]/.test(c)) {
      let j = i;
      while (j < src.length && /[0-9.]/.test(src[j]!)) j += 1;
      const n = Number(src.slice(i, j));
      if (!Number.isFinite(n)) return { code: 'FORMULA_BAD_NUMBER', message: `숫자를 읽을 수 없습니다: ${src.slice(i, j)}` };
      out.push({ t: 'num', v: n });
      i = j;
      continue;
    }
    // 함수 이름 — 한글·영문·밑줄
    if (/[A-Za-z_가-힣]/.test(c)) {
      let j = i;
      while (j < src.length && /[A-Za-z0-9_가-힣]/.test(src[j]!)) j += 1;
      const word = src.slice(i, j);
      const name = FN_ALIAS[word.toUpperCase()] ?? FN_ALIAS[word];
      if (!name) return { code: 'FORMULA_UNKNOWN_FN', message: `모르는 함수입니다: ${word}` };
      out.push({ t: 'fn', name });
      i = j;
      continue;
    }
    return { code: 'FORMULA_BAD_CHAR', message: `쓸 수 없는 글자입니다: ${c}` };
  }
  return out;
}

// ── 나무 ────────────────────────────────────────────────────────────
export type Ast =
  | { k: 'num'; v: number }
  | { k: 'ref'; field: string; column: string | null }
  | { k: 'fn'; name: string; args: Ast[] }
  | { k: 'bin'; op: '+' | '-' | '*' | '/'; l: Ast; r: Ast }
  | { k: 'neg'; e: Ast };

/**
 * 식을 읽는다 — **저장 전에** 본다. 깨진 식이 들어가면 그 양식으로 아무도 기안할 수 없다.
 */
export function parseFormula(src: string): { ok: true; ast: Ast } | { ok: false; problem: FormulaError } {
  if (typeof src !== 'string' || src.trim() === '') {
    return { ok: false, problem: { code: 'FORMULA_EMPTY', message: '계산식이 비어 있습니다' } };
  }
  if (src.length > FORMULA_MAX_LEN) {
    return { ok: false, problem: { code: 'FORMULA_TOO_LONG', message: `계산식은 ${FORMULA_MAX_LEN}자까지입니다` } };
  }
  const toks = tokenize(src);
  if (!Array.isArray(toks)) return { ok: false, problem: toks };

  let p = 0;
  const peek = () => toks[p];
  let failed: FormulaError | null = null;
  const fail = (code: string, message: string): Ast => {
    if (!failed) failed = { code, message };
    return { k: 'num', v: 0 };
  };

  const expr = (): Ast => {
    let node = term();
    for (;;) {
      const t = peek();
      if (t && t.t === 'op' && (t.v === '+' || t.v === '-')) { p += 1; node = { k: 'bin', op: t.v, l: node, r: term() }; }
      else return node;
    }
  };
  const term = (): Ast => {
    let node = unary();
    for (;;) {
      const t = peek();
      if (t && t.t === 'op' && (t.v === '*' || t.v === '/')) { p += 1; node = { k: 'bin', op: t.v, l: node, r: unary() }; }
      else return node;
    }
  };
  const unary = (): Ast => {
    const t = peek();
    if (t && t.t === 'op' && t.v === '-') { p += 1; return { k: 'neg', e: unary() }; }
    return atom();
  };
  const atom = (): Ast => {
    const t = peek();
    if (!t) return fail('FORMULA_UNEXPECTED_END', '계산식이 도중에 끝났습니다');
    if (t.t === 'num') { p += 1; return { k: 'num', v: t.v }; }
    if (t.t === 'ref') { p += 1; return { k: 'ref', field: t.field, column: t.column }; }
    if (t.t === '(') {
      p += 1;
      const e = expr();
      if (peek()?.t !== ')') return fail('FORMULA_UNCLOSED', '괄호가 닫히지 않았습니다');
      p += 1;
      return e;
    }
    if (t.t === 'fn') {
      p += 1;
      if (peek()?.t !== '(') return fail('FORMULA_FN_NO_PAREN', `«${t.name}» 뒤에 괄호가 있어야 합니다`);
      p += 1;
      const args: Ast[] = [];
      if (peek()?.t !== ')') {
        for (;;) {
          args.push(expr());
          if (peek()?.t === ',') { p += 1; continue; }
          break;
        }
      }
      if (peek()?.t !== ')') return fail('FORMULA_UNCLOSED', '괄호가 닫히지 않았습니다');
      p += 1;
      const [min, max] = FN_ARITY[t.name]!;
      if (args.length < min || args.length > max) {
        return fail('FORMULA_FN_ARITY', `«${t.name}» 은(는) 인자가 ${min === max ? `${min}개` : `${min}~${max}개`}여야 합니다`);
      }
      return { k: 'fn', name: t.name, args };
    }
    return fail('FORMULA_UNEXPECTED', '계산식을 읽을 수 없습니다');
  };

  const ast = expr();
  if (failed) return { ok: false, problem: failed };
  if (p !== toks.length) return { ok: false, problem: { code: 'FORMULA_TRAILING', message: '계산식 뒤에 읽지 못한 것이 남았습니다' } };
  return { ok: true, ast };
}

/** 식이 가리키는 칸들 — 「그 칸이 있나」를 부르는 쪽이 본다. */
export function formulaRefs(ast: Ast): FormulaRefs {
  const fields = new Set<string>();
  const columns: Array<{ field: string; column: string }> = [];
  const walk = (n: Ast): void => {
    if (n.k === 'ref') {
      if (n.column == null) fields.add(n.field);
      else columns.push({ field: n.field, column: n.column });
      return;
    }
    if (n.k === 'fn') { n.args.forEach(walk); return; }
    if (n.k === 'bin') { walk(n.l); walk(n.r); return; }
    if (n.k === 'neg') walk(n.e);
  };
  walk(ast);
  return { fields: [...fields], columns };
}

// ── 셈 ──────────────────────────────────────────────────────────────

/** `1,200` · `₩30,000` 처럼 적혀도 읽는다. 못 읽으면 `null` — **0 으로 보지 않는다**. */
export function readNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  const s = v.replace(/[,\s₩]/g, '');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
/** `HH:MM` → 자정부터의 분. 모양이 다르면 `null`. */
function readMinutes(v: unknown): number | null {
  const m = TIME_RE.exec(typeof v === 'string' ? v : '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
function readDay(v: unknown): number | null {
  const m = DATE_RE.exec(typeof v === 'string' ? v : '');
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(t) ? Math.floor(t / 86400000) : null;
}

/**
 * 식을 센다.
 *
 * ★★ **모르면 `null` 이 위로 번진다** — 한 칸이 비면 그 식 전체가 빈 값이다.
 *    (합계만 예외가 아니다: 빈 표의 합계도 «0» 이 아니라 «아직 없음» 이다.)
 */
export function evalFormula(ast: Ast, ctx: FormulaContext): FormulaResult {
  let unread = 0;
  let read = 0;

  const go = (n: Ast): number | null => {
    switch (n.k) {
      case 'num': return n.v;
      case 'neg': { const v = go(n.e); return v == null ? null : -v; }
      case 'bin': {
        const l = go(n.l); const r = go(n.r);
        if (l == null || r == null) return null;
        if (n.op === '+') return l + r;
        if (n.op === '-') return l - r;
        if (n.op === '*') return l * r;
        // ★ 0 으로 나누면 «무한» 이 아니라 «모름» 이다 — 화면에 Infinity 를 적을 수는 없다.
        return r === 0 ? null : l / r;
      }
      case 'ref': {
        if (n.column != null) return null;  // 열 참조는 함수 안에서만 뜻이 있다
        const raw = ctx.field(n.field);
        if (raw === undefined || raw === null || raw === '') return null;
        read += 1;
        const num = readNumber(raw);
        if (num == null) unread += 1;
        return num;
      }
      case 'fn': return fn(n);
    }
  };

  const colOf = (a: Ast): string[] | null =>
    a.k === 'ref' && a.column != null ? ctx.column(a.field, a.column) : null;

  const fn = (n: Extract<Ast, { k: 'fn' }>): number | null => {
    if (n.name === '합계' || n.name === '개수') {
      const cells = colOf(n.args[0]!);
      if (cells == null) return null;
      const filled = cells.filter((c) => c.trim() !== '');
      if (filled.length === 0) return null;   // 빈 표는 «0» 이 아니라 «아직 없음»
      if (n.name === '개수') { read += filled.length; return filled.length; }
      let sum = 0;
      let got = 0;
      for (const c of filled) {
        read += 1;
        const v = readNumber(c);
        if (v == null) { unread += 1; continue; }
        sum += v;
        got += 1;
      }
      /**
       * ★★★ **한 줄도 못 읽었으면 «0» 이 아니라 «모름» 이다.** 「금액」 열이 통째로
       *    「3만원」·「미정」 처럼 적혀 있으면 합계는 0 원이 아니라 **아직 없는 값**이다 —
       *    0 으로 주면 그 문서가 «50만원 이하» 로 판정돼 **전결로 빠져나간다**.
       *    (검사가 실제로 이 자리를 잡았다: 2026-09-01.)
       */
      return got === 0 ? null : sum;
    }
    if (n.name === '시간차') {
      const a = n.args[0]!; const b = n.args[1]!;
      const start = a.k === 'ref' && a.column == null ? readMinutes(ctx.field(a.field)) : null;
      const end = b.k === 'ref' && b.column == null ? readMinutes(ctx.field(b.field)) : null;
      if (start == null || end == null) return null;
      read += 2;
      // ★ 끝이 시작보다 이르면 **자정을 넘긴 것**으로 본다(야간 근무) — 음수로 두면 −21시간이 된다.
      const diff = end >= start ? end - start : end + 1440 - start;
      return diff / 60;
    }
    if (n.name === '일수') {
      const a = n.args[0]!;
      if (a.k !== 'ref' || a.column != null) return null;
      const raw = ctx.field(a.field);
      const p = (typeof raw === 'object' && raw !== null ? raw : {}) as { start?: unknown; end?: unknown };
      const s = readDay(p.start); const e = readDay(p.end);
      if (s == null || e == null || e < s) return null;
      read += 1;
      return e - s + 1;   // ★ 시작과 끝을 양쪽 다 센다 — 하루짜리는 1일이다
    }
    // 반올림
    const x = go(n.args[0]!);
    if (x == null) return null;
    const digits = n.args[1] ? go(n.args[1]) : 0;
    const d = digits == null ? 0 : Math.max(0, Math.min(6, Math.trunc(digits)));
    const f = 10 ** d;
    return Math.round(x * f) / f;
  };

  const value = go(ast);
  return { value: value == null || !Number.isFinite(value) ? null : value, unread, read };
}

/** 식 한 줄을 바로 센다 — 부르는 쪽이 파싱을 따로 안 하게. */
export function computeFormula(expr: string, ctx: FormulaContext): FormulaResult {
  const parsed = parseFormula(expr);
  if (!parsed.ok) return { value: null, unread: 0, read: 0 };
  return evalFormula(parsed.ast, ctx);
}
